class ShopTheLook {
  constructor(section) {
    this.section = section;
    this.grid = section.querySelector('.shop-the-look__grid');
    this.drawer = section.querySelector('[data-shop-the-look-drawer]');
    this.drawerContent = section.querySelector('[data-shop-the-look-drawer-content]');
    this.activePanel = null;
    this.activeTrigger = null;
    this.panelHome = null;
    this.movedPanelInner = null;
    this.isClosingPanel = false;
    this.panelFadeMs = 400;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.onDrawerScroll = this.onDrawerScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onVariantClick = this.onVariantClick.bind(this);
    this.onAddToCart = this.onAddToCart.bind(this);
    this.onAddAll = this.onAddAll.bind(this);
    this.onSliderArrow = this.onSliderArrow.bind(this);

    this.bindEvents();
  }

  bindEvents() {
    this.section.addEventListener('click', this.onTriggerClick);
    this.section.addEventListener('click', this.onDrawerClose);
    this.section.addEventListener('click', this.onDrawerScroll);
    this.section.addEventListener('click', this.onVariantClick);
    this.section.addEventListener('click', this.onAddToCart);
    this.section.addEventListener('click', this.onAddAll);
    this.section.addEventListener('click', this.onSliderArrow);
    document.addEventListener('keydown', this.onKeydown);
  }

  isMobile() {
    return window.innerWidth < 767;
  }

  getColumns() {
    return this.isMobile() ? 2 : 3;
  }

  getPanelForTrigger(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.section.querySelector('#' + id) : null;
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-shop-the-look-trigger]');
    if (!trigger || !this.section.contains(trigger) || trigger.disabled) return;

    event.preventDefault();

    if (this.activeTrigger === trigger && this.activePanel) {
      this.closeActive();
      return;
    }

    if (this.isClosingPanel) return;

    this.openPanel(trigger);
  }

  openPanel(trigger) {
    const panel = this.getPanelForTrigger(trigger);
    if (!panel) return;

    const hadOpen = !!this.activePanel || this.drawer.classList.contains('is-open');
    this.closeActive({ instant: hadOpen });

    this.activeTrigger = trigger;
    this.activePanel = panel;
    this.panelHome = panel;

    if (this.isMobile()) {
      this.openMobileDrawer(panel, trigger);
    } else {
      this.openDesktopPanel(panel, trigger);
    }
  }

  openDesktopPanel(panel, trigger) {
    this.positionPanel(panel, trigger);
    trigger.setAttribute('aria-expanded', 'true');

    const inner = panel.querySelector('.shop-the-look__panel-inner');
    const $ = window.jQuery;

    if ($) {
      $(panel).stop(true, true).removeAttr('hidden').hide().fadeIn(this.panelFadeMs);
    } else {
      panel.removeAttribute('hidden');
    }

    this.initSliders(inner);
    this.updateAddAllCount(inner);
  }

  openMobileDrawer(panel, trigger) {
    const panelInner = panel.querySelector('.shop-the-look__panel-inner');
    if (!panelInner) return;

    this.movedPanelInner = panelInner;
    panelInner.classList.add('is-drawer');
    this.drawerContent.appendChild(panelInner);

    this.drawer.removeAttribute('hidden');
    this.drawer.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
    });

    document.body.style.overflow = 'hidden';
    trigger.setAttribute('aria-expanded', 'true');

    this.updateAddAllCount(panelInner);
  }

  positionPanel(panel, trigger) {
    const wraps = Array.prototype.slice.call(this.grid.querySelectorAll('.shop-the-look__card-wrap'));
    const index = wraps.findIndex(function (wrap) {
      return wrap.contains(trigger);
    });

    if (index === -1) return;

    const cols = this.getColumns();
    const rowEnd = Math.min(Math.floor(index / cols) * cols + cols - 1, wraps.length - 1);
    const anchor = wraps[rowEnd];

    if (anchor.nextSibling !== panel) {
      this.grid.insertBefore(panel, anchor.nextSibling);
    }
  }

  resetDesktopPanelStyles(panel) {
    if (!panel || !window.jQuery) return;
    window.jQuery(panel).css({ opacity: '', display: '' });
  }

  closeActive(options) {
    options = options || {};
    const instant = options.instant === true;

    if (!this.activePanel && !this.drawer.classList.contains('is-open')) return;
    if (this.isClosingPanel && !instant) return;

    const $ = window.jQuery;
    const panel = this.activePanel;
    const trigger = this.activeTrigger;
    const isDesktopPanel = panel && !this.movedPanelInner && !this.isMobile();

    if (this.movedPanelInner && this.panelHome) {
      this.movedPanelInner.classList.remove('is-drawer');
      this.panelHome.appendChild(this.movedPanelInner);
      this.movedPanelInner = null;
    }

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('hidden', '');
    document.body.style.overflow = '';

    const finalize = () => {
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      this.activePanel = null;
      this.activeTrigger = null;
      this.panelHome = null;
      this.isClosingPanel = false;
    };

    if (isDesktopPanel && $ && !instant) {
      this.isClosingPanel = true;
      $(panel).stop(true).fadeOut(this.panelFadeMs, () => {
        panel.setAttribute('hidden', '');
        this.resetDesktopPanelStyles(panel);
        finalize();
      });
      return;
    }

    if (panel) {
      if ($) $(panel).stop(true, true);
      panel.setAttribute('hidden', '');
      this.resetDesktopPanelStyles(panel);
    }

    finalize();
  }

  onDrawerClose(event) {
    if (!event.target.closest('[data-shop-the-look-drawer-close]')) return;
    event.preventDefault();
    this.closeActive();
  }

  onDrawerScroll(event) {
    const button = event.target.closest('[data-shop-the-look-drawer-scroll]');
    if (!button || !this.drawer.classList.contains('is-open')) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeActive();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && (this.activePanel || this.drawer.classList.contains('is-open'))) {
      this.closeActive();
    }
  }

  onVariantClick(event) {
    const toggle = event.target.closest('[data-stl-variant-toggle]');
    if (!toggle || toggle.disabled || !this.section.contains(toggle)) return;

    event.preventDefault();
    event.stopPropagation();

    const card = toggle.closest('[data-stl-product-card]');
    if (!card) return;

    Array.prototype.forEach.call(card.querySelectorAll('[data-stl-variant-toggle]'), (button) => {
      const isSelected = button === toggle;
      button.classList.toggle('stl-product-card__variant--selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const container = this.getActiveContainer();
    if (container) this.updateAddAllCount(container);
  }

  checkOrderTypeConflict(orderType) {
    if (!window.theme || !window.theme.OrderTypeGuard) return false;
    const conflictType = theme.OrderTypeGuard.conflict(orderType || 'standard');
    if (conflictType) {
      theme.OrderTypeGuard.openModal(conflictType, orderType || 'standard');
      return true;
    }
    return false;
  }

  onAddToCart(event) {
    const button = event.target.closest('[data-stl-add-to-cart]');
    if (!button || button.disabled || !this.section.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const card = button.closest('[data-stl-product-card]');
    const ids = this.getSelectedIdsFromCard(card);

    if (!ids.length) return;

    const orderType = card ? (card.dataset.orderType || 'standard') : 'standard';
    if (this.checkOrderTypeConflict(orderType)) return;

    button.disabled = true;
    this.addItemsToCart(ids).then(
      function () { button.disabled = false; },
      function () { button.disabled = false; }
    );
  }

  onAddAll(event) {
    const button = event.target.closest('[data-shop-the-look-add-all]');
    if (!button || button.disabled || !this.section.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const container = button.closest('.shop-the-look__panel-inner');
    if (!container) return;

    const row = container.querySelector('[data-shop-the-look-row]');
    const ids = this.getSelectedIdsFromRow(row);
    if (!ids.length) return;

    const cards = row ? row.querySelectorAll('[data-stl-product-card]') : [];
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (this.getSelectedIdsFromCard(card).length === 0) continue;
      const orderType = card.dataset.orderType || 'standard';
      if (this.checkOrderTypeConflict(orderType)) return;
    }

    button.disabled = true;
    this.addItemsToCart(ids).then(
      function () { button.disabled = false; },
      function () { button.disabled = false; }
    );
  }

  onSliderArrow(event) {
    const arrow = event.target.closest('.stl-product-card__slider-arrow');
    if (!arrow || !this.section.contains(arrow)) return;

    event.preventDefault();
    event.stopPropagation();

    if (!window.jQuery) return;

    const card = arrow.closest('.stl-product-card');
    if (!card) return;

    const sliderEl = card.querySelector('.stl-product-card__slider.slick-initialized');
    if (sliderEl) window.jQuery(sliderEl).slick('slickNext');
  }

  getActiveContainer() {
    if (this.isMobile() && this.movedPanelInner) return this.movedPanelInner;
    if (this.activePanel) return this.activePanel.querySelector('.shop-the-look__panel-inner');
    return null;
  }

  getSelectedIdsFromCard(card) {
    if (!card) return [];

    const selected = Array.prototype.slice.call(
      card.querySelectorAll('[data-stl-variant-toggle].stl-product-card__variant--selected')
    );

    if (selected.length) {
      return selected.map(function (toggle) {
        return toggle.dataset.variantId;
      });
    }

    const defaultVariant = card.querySelector('[data-stl-default-variant]');
    if (defaultVariant) return [defaultVariant.value];

    return [];
  }

  getSelectedIdsFromRow(row) {
    if (!row) return [];

    const ids = [];

    Array.prototype.forEach.call(row.querySelectorAll('[data-stl-product-card]'), (card) => {
      Array.prototype.push.apply(ids, this.getSelectedIdsFromCard(card));
    });

    return ids;
  }

  updateAddAllCount(container) {
    if (!container) return;

    const row = container.querySelector('[data-shop-the-look-row]');
    const addAllBtn = container.querySelector('[data-shop-the-look-add-all]');
    const countEl = container.querySelector('[data-shop-the-look-add-all-count]');

    if (!row || !addAllBtn || !countEl) return;

    const count = this.getSelectedIdsFromRow(row).length;

    countEl.textContent = String(count);
    addAllBtn.disabled = count === 0;

    Array.prototype.forEach.call(row.querySelectorAll('[data-stl-add-to-cart]'), (button) => {
      const card = button.closest('[data-stl-product-card]');
      button.disabled = this.getSelectedIdsFromCard(card).length === 0;
    });
  }

  setSliderProgress(sliderEl, slideIndex, slideCount, fromInit) {
    const card = sliderEl.closest('.stl-product-card');
    if (!card) return;

    const progressBar = card.querySelector('.stl-product-card__progress span');
    if (!progressBar || slideCount <= 0) return;

    let calc;

    if (fromInit) {
      calc = (1 / slideCount) * 100;
    } else if (slideCount === 1) {
      calc = 100;
    } else {
      calc = (slideIndex / (slideCount - 1)) * 100;
    }

    progressBar.style.width = calc + '%';
  }

  initSliders(container) {
    if (!container || !window.jQuery || !window.jQuery.fn.slick) return;
    if (container.closest('[data-shop-the-look-drawer-content]')) return;

    const $ = window.jQuery;
    const sliderEls = container.querySelectorAll('.stl-product-card__slider:not(.slick-initialized)');
    if (!sliderEls.length) return;

    const isDesktop = window.innerWidth > 990;
    const settings = {
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 400,
      fade: true,
      draggable: false,
      cssEase: 'linear',
      arrows: false,
      dots: false,
      initialSlide: isDesktop ? 1 : 0,
    };

    Array.prototype.forEach.call(sliderEls, (sliderEl) => {
      const $slider = $(sliderEl);

      if (isDesktop) {
        $slider.on('init', (event, slick) => {
          this.setSliderProgress(sliderEl, 0, slick.slideCount, true);
        });
      }

      $slider.slick(settings);

      $slider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
        this.setSliderProgress(sliderEl, nextSlide, slick.slideCount, false);
      });
    });
  }

  addItemsToCart(variantIds) {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const items = variantIds.map(function (id) {
      return { id: Number(id), quantity: 1 };
    });

    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart add failed');
        return response.json();
      })
      .then(function () {
        const miniCart = document.querySelector('mini-cart');
        if (miniCart) miniCart.updateView(true);
      })
      .catch(function (error) {
        console.error(error);
      });
  }
}

function initShopTheLook(scope) {
  Array.prototype.forEach.call(
    (scope || document).querySelectorAll('[data-section-type="shop-the-look"]'),
    (section) => {
      if (section.shopTheLook) return;
      section.shopTheLook = new ShopTheLook(section);
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  initShopTheLook();
});

document.addEventListener('shopify:section:load', (event) => {
  initShopTheLook(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('[data-section-type="shop-the-look"]') || event.target;
  if (section.shopTheLook) {
    section.shopTheLook.closeActive({ instant: true });
    section.shopTheLook = null;
  }
});
